import React from "react";
import { motion } from "framer-motion";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

/** Cinematic engine header used by the Authorization / SU24 / OData modules:
 * a scanning sweep over the engine identity, the live detection tally, and the
 * rule catalogue — so each engine page opens like a console coming online. */
export function EngineHero({ icon: Icon, engine, title, blurb, rules, totals, testId }) {
    const total = Object.values(totals || {}).reduce((a, b) => a + b, 0);
    return (
        <motion.section
            data-testid={testId}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="panel-elevated chrome-bevel relative overflow-hidden rounded-[4px]"
        >
            <div className="scanline absolute inset-0 pointer-events-none opacity-40" />
            <div
                className="absolute -right-24 -top-24 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                style={{ background: "rgb(var(--accent-1-rgb) / 0.07)" }}
            />

            <div className="relative grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6 p-6">
                <div>
                    <div className="flex items-center gap-3">
                        <span
                            className="w-10 h-10 rounded-sm flex items-center justify-center border shrink-0"
                            style={{
                                borderColor: "rgb(var(--accent-1-rgb) / 0.32)",
                                background: "rgb(var(--accent-1-rgb) / 0.08)",
                            }}
                        >
                            <Icon size={18} style={{ color: "rgb(var(--accent-1-rgb))" }} strokeWidth={1.7} />
                        </span>
                        <div>
                            <div className="label-eyebrow">Engine · {engine}</div>
                            <h2 className="font-display text-lg text-white tracking-tight">{title}</h2>
                        </div>
                    </div>

                    <p className="text-sm text-slate-400 mt-4 leading-relaxed max-w-2xl">{blurb}</p>

                    <div className="flex items-baseline gap-6 mt-6">
                        <div>
                            <div className="font-display text-4xl text-white leading-none">
                                <AnimatedNumber value={total} />
                            </div>
                            <div className="label-eyebrow mt-1.5">Detections</div>
                        </div>
                        <div className="h-10 w-px bg-white/8" />
                        <div>
                            <div className="font-display text-4xl text-white leading-none">{rules.length}</div>
                            <div className="label-eyebrow mt-1.5">Active rules</div>
                        </div>
                        <div className="h-10 w-px bg-white/8" />
                        <div>
                            <div className="font-display text-4xl leading-none" style={{ color: "rgb(var(--accent-1-rgb))" }}>
                                {Object.keys(totals || {}).length}
                            </div>
                            <div className="label-eyebrow mt-1.5">Categories hit</div>
                        </div>
                    </div>
                </div>

                <div className="lg:border-l lg:border-white/8 lg:pl-6">
                    <div className="label-eyebrow mb-3">Rule catalogue</div>
                    <ul className="space-y-1.5">
                        {rules.map((r, i) => {
                            const hits = (totals || {})[r.category] ?? null;
                            return (
                                <motion.li
                                    key={r.label}
                                    initial={{ opacity: 0, x: 12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.18 + i * 0.055, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                    className="flex items-center gap-2.5 text-[13px]"
                                >
                                    <span
                                        className="w-1 h-1 rounded-full shrink-0"
                                        style={{
                                            background: hits ? "var(--neon-red)" : "rgba(148,163,184,0.45)",
                                            boxShadow: hits ? "0 0 6px var(--neon-red)" : "none",
                                        }}
                                    />
                                    <span className={hits ? "text-slate-200" : "text-slate-500"}>{r.label}</span>
                                    <span className="ml-auto font-mono text-[11px] tabular-nums shrink-0" style={{ color: hits ? "rgb(var(--accent-1-rgb))" : "rgba(148,163,184,0.4)" }}>
                                        {hits ?? "—"}
                                    </span>
                                </motion.li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </motion.section>
    );
}
